import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { m, AnimatePresence } from "framer-motion";
import MultiStepForm, { FieldProps, variantsChildren } from "./form";
import SuccessMessage from "./successMessage";
import { sendGTMEvent } from "@next/third-parties/google";
import { launchConfetti } from "@/app/lib/confetti";
import { normalizePhoneToE164 } from "@/app/lib/phone";
import { formatPragueTimestamp } from "@/app/lib/formatTime";
import { buildAttributionSnapshot } from "@/app/lib/attributionSnapshot";

export interface FormWizardStepProps {
  id: string;
  fields: Array<FieldProps>;
}

const steps: FormWizardStepProps[] = [
  {
    id: "contact",
    fields: [
      { name: "name", type: "text", required: true },
      { name: "company", type: "text", required: false },
      { name: "email", type: "email", required: true },
      { name: "phone", type: "tel", required: false },
      { name: "message", type: "textarea", required: true },
      { name: "acceptTerms", type: "checkbox", required: true },
    ],
  },
];

const FormContact: React.FC = () => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useTranslations("form");
  const [submitting, setSubmitting] = useState(false);

  const defaultValues = {};

  const handleSubmit = async (values: Record<string, any>) => {
    const apiEndpoint = "/api/contact-email";
    setSuccess(false);
    setError(null);
    setSubmitting(true);

    const normalizedPhone = normalizePhoneToE164(values.phone);
    const nameTrimmed = (values.name || "").trim();
    const nameParts = nameTrimmed ? nameTrimmed.split(/\s+/) : [];
    const firstName = nameParts[0] || null;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : null;
    const company = (values.company || "").trim();

    const payload = {
      ...values,
      phone: normalizedPhone ?? values.phone ?? null,
      company: company || null,
      first_name: firstName,
      last_name: lastName,
      form_name: "contact",
      form_submit_time: formatPragueTimestamp(),
      attribution_snapshot: buildAttributionSnapshot(),
    };

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!responseData.success) {
        setError(responseData.errors);
        setSubmitting(false);
        return Promise.reject(responseData.errors); // 👈 reject for hook-form to track failure
      } else {
        setSuccess(true);
        setSubmitting(false);
        sendGTMEvent({
          event: "generate_lead",
          form_name: "contact",
        });
        launchConfetti();
        return Promise.resolve(); // 👈 resolve on success
      }
    } catch (err: any) {
      setError("Network or server error: " + err.message);
      setSubmitting(false);
      return Promise.reject(err); // 👈 same here
    }
  };

  const [values, setValues] = useState<Record<string, any>>({});
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const activeStep = useMemo(() => steps[activeStepIndex], [activeStepIndex]);
  const isLastStep = useMemo(
    () => activeStepIndex === steps.length - 1,
    [activeStepIndex],
  );

  const goNextStep = () => {
    setActiveStepIndex((index) => (index += 1));
  };

  const goPrevStep = () => {
    setActiveStepIndex((index) => (index -= 1));
  };

  const handleNextStep = (stepValues: Record<string, any>) => {
    const newValues = { ...values, ...stepValues };
    setValues(newValues);

    if (isLastStep) {
      handleSubmit(newValues);
    } else {
      goNextStep();
    }
  };

  if (!activeStep) {
    return null;
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <m.div
        key={success ? "success" : activeStep.id}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ staggerChildren: 0.1 }}
        className="min-h-full flex flex-col justify-center items-center w-full"
      >
        {success ? (
          <SuccessMessage closeButton={false} />
        ) : (
          <m.div
            key={activeStep.id}
            variants={variantsChildren}
            className="w-full max-w-md flex flex-col items-center gap-8"
          >
            {" "}
            <h3 className="h4 uppercase text-center">
              {t(`${activeStep.id}Title`)}
            </h3>
            <MultiStepForm
              activeStepId={activeStep.id}
              key={activeStep.id} // IMPORTANT! Keep form instances separate
              fields={activeStep.fields}
              defaultValues={{ ...defaultValues, ...values }}
              showBackButton={false}
              onSubmit={handleNextStep}
              onBack={() => null}
              isLastStep={activeStepIndex === steps.length - 1}
              submitting={submitting}
            />
            {error && (
              <p className="text-red-500 text-sm text-center">
                {String(error)}
              </p>
            )}
          </m.div>
        )}
      </m.div>
    </AnimatePresence>
  );
};
export default FormContact;
